/**
 * The first languages the signup form offers, and the escape hatch for the
 * rest.
 *
 * Alphabetical, not most-common-first: any popularity ordering is a guess
 * about a user base this project cannot yet observe, and it ages into a claim
 * nobody re-checks. English is absent because every learner here is learning
 * it. The list is a product call (design §9) — adjust it here, not at a call
 * site.
 *
 * The names are what gets stored. `prompt.rs` interpolates the stored string
 * straight into the system prompt, and `language.rs` records that
 * `native_language` holds a display name while `target_language` holds a code;
 * design §5.3 defers resolving that asymmetry.
 */

export type NativeLanguage = {
  name: string;
  /**
   * Absent when this build's recogniser has no such language. An entry with no
   * code is offered on the form and simply never matches a detected language,
   * which is the correct behaviour for one the recogniser cannot report.
   */
  code?: string;
  /**
   * The catalog its speakers read, a BCP 47 tag for the written language
   * (native-language UI spec §3.3), whether or not it has shipped: it also
   * names the language in `languageLabel`. `tutor_core::locale` copies the
   * rows whose catalog has shipped, and `languages.test.ts` holds the two
   * tables to that.
   */
  locale: string;
};

/**
 * `code` lives here rather than in the one feature that needs it.
 *
 * The comprehension aids offer "say this in English" when a learner's turn
 * comes back in their own first language. That compares the recogniser's code
 * on the turn (`vi`) against the profile's display name (`Vietnamese`), so
 * something has to map between them — and which code a language has is a
 * property of the language, not of that offer. A private copy over there would
 * keep compiling and quietly stop matching the first time an entry here is
 * re-spelled.
 *
 * Amendment: that comparison no longer happens here, or anywhere in the
 * client. A review of the comprehension aids feature found the client's copy
 * of it (`languageCodeFor`, an exact-name lookup) disagreeing with the
 * server's own isolang-based comparison for three of these entries (`Chinese
 * (Mandarin)`, `Filipino`, `Punjabi`) — two different implementations of one
 * rule, and a Mandarin speaker's Mandarin turn was mislabelled as a result.
 * The comparison now lives once, server-side (`aids::translation_target`),
 * and `client/src/views/aids.ts` reads its answer rather than deriving one,
 * so `code` has no current caller in the client. Left in place rather than
 * removed: which code a language has is still a property of the language,
 * not of the offer that stopped needing it here, and the measurement below
 * is true regardless of who reads it.
 *
 * The codes are ISO 639-1 where one exists, and the two awkward ones were
 * measured against this build's recogniser rather than assumed.
 *
 * The measurement matters because the obvious probe does not work: forcing
 * `language=X` on a transcription echoes X back in the response, so `zz` comes
 * back as `zz` and proves nothing. What does discriminate is the decoding —
 * a code the model knows changes punctuation, segment end or the leading
 * space against a nonsense control on the same audio. `tl` (Filipino) does;
 * `yue` did not, matching `zz` byte for byte.
 *
 * Our STT call forces no language at all (`tutor-omlx/src/stt.rs` sends only
 * model, word_timestamps and response_format), so a detected language is
 * genuine auto-detection and comes back as a bare lowercase code.
 */
export const NATIVE_LANGUAGES: readonly NativeLanguage[] = [
  { name: 'Arabic', code: 'ar', locale: 'ar' },
  { name: 'Bengali', code: 'bn', locale: 'bn' },
  // No code: `yue` is its 639-3 and there is no 639-1, but this build's
  // recogniser does not know `yue` -- it decoded identically to a nonsense
  // control. `zh` would fire, and would fire on Mandarin too; an offer to
  // translate that appears on the wrong language is worse than one that stays
  // quiet, so Cantonese is offered on the form and never matched.
  // oxlint-disable-next-line tutor/no-english -- a stored value; `languageLabel` labels the option (spec §4.6)
  { name: 'Chinese (Cantonese)', locale: 'zh-Hant' },
  // oxlint-disable-next-line tutor/no-english -- a stored value; `languageLabel` labels the option (spec §4.6)
  { name: 'Chinese (Mandarin)', code: 'zh', locale: 'zh-Hans' },
  { name: 'Dutch', code: 'nl', locale: 'nl' },
  { name: 'Filipino', code: 'tl', locale: 'fil' },
  { name: 'French', code: 'fr', locale: 'fr' },
  { name: 'German', code: 'de', locale: 'de' },
  { name: 'Hindi', code: 'hi', locale: 'hi' },
  { name: 'Indonesian', code: 'id', locale: 'id' },
  { name: 'Italian', code: 'it', locale: 'it' },
  { name: 'Japanese', code: 'ja', locale: 'ja' },
  { name: 'Korean', code: 'ko', locale: 'ko' },
  { name: 'Malay', code: 'ms', locale: 'ms' },
  { name: 'Persian', code: 'fa', locale: 'fa' },
  { name: 'Polish', code: 'pl', locale: 'pl' },
  { name: 'Portuguese', code: 'pt', locale: 'pt-BR' },
  { name: 'Punjabi', code: 'pa', locale: 'pa' },
  { name: 'Russian', code: 'ru', locale: 'ru' },
  { name: 'Spanish', code: 'es', locale: 'es' },
  { name: 'Tamil', code: 'ta', locale: 'ta' },
  { name: 'Telugu', code: 'te', locale: 'te' },
  { name: 'Thai', code: 'th', locale: 'th' },
  { name: 'Turkish', code: 'tr', locale: 'tr' },
  { name: 'Ukrainian', code: 'uk', locale: 'uk' },
  { name: 'Urdu', code: 'ur', locale: 'ur' },
  { name: 'Vietnamese', code: 'vi', locale: 'vi' },
];

/**
 * The select's label for a curated language (spec §4.6): its name in the
 * interface language, then its own name for itself, so a learner finds their
 * language by the name they know it by. English keeps the curated name, which
 * is also the stored value; elsewhere the platform's `Intl.DisplayNames` names
 * it. Anything not on the list is shown as stored.
 */
export function languageLabel(name: string, ui: string): string {
  const entry = NATIVE_LANGUAGES.find((l) => l.name === name);
  if (!entry) return name;
  const own = displayName(spokenTag(entry), spokenTag(entry));
  const local = languageName(name, ui);
  return own && own !== local ? `${local} — ${own}` : local;
}

/**
 * A language the server names in English (`Translation.language`, the stored
 * native language), named in the interface language instead. English itself
 * is one of them: a translation into English is the aids' other direction.
 */
export function languageName(name: string, ui: string): string {
  if (ui === 'en' || ui === 'qps') return name;
  const entry = NATIVE_LANGUAGES.find((l) => l.name === name);
  const tag = entry ? spokenTag(entry) : name === 'English' ? 'en' : null;
  return (tag && displayName(tag, ui)) ?? name;
}

/**
 * The name of a language as `Intl.DisplayNames` knows it. Cantonese is named
 * by its spoken code: its catalog is written Traditional Chinese, which the
 * platform would call "Traditional Chinese", a script and not the language.
 */
function spokenTag(entry: NativeLanguage): string {
  return entry.locale === 'zh-Hant' ? 'yue' : entry.locale.split('-')[0]!;
}

function displayName(tag: string, ui: string): string | undefined {
  try {
    return new Intl.DisplayNames([ui], { type: 'language', fallback: 'none' }).of(tag);
  } catch {
    return undefined;
  }
}

/** The names alone, for a control that only renders them. */
export const LANGUAGE_NAMES: readonly string[] = NATIVE_LANGUAGES.map((l) => l.name);

/**
 * The select's "something else" value.
 *
 * Deliberately neither a real language name nor the empty string: the empty
 * string is "prefer not to say", and the two have to stay distinguishable or
 * choosing Other and typing nothing would silently mean "declined".
 */
export const OTHER_LANGUAGE = '__other__';

/**
 * The recogniser's code for a stored display name, if it is one this list
 * knows.
 *
 * `undefined` for anything typed into the Other box, which is the honest
 * answer rather than a gap: an unlisted language has no code here, and a
 * caller must not guess one. A consumer comparing against a detected language
 * should treat `undefined` as "no opinion" and stay quiet.
 */
export function languageCodeFor(name: string): string | undefined {
  return NATIVE_LANGUAGES.find((l) => l.name === name)?.code;
}

/** Whether a stored language is one the select can display. */
export function isListedLanguage(name: string): boolean {
  return NATIVE_LANGUAGES.some((l) => l.name === name);
}
